/**
 * 사용자·세션 목 데이터. 출처: 와이어프레임 `ROLES`(935행) · 관리 › 사용자 표 `M`(2655행) · `SESS`(1404행).
 *
 * 🔴 **역할이 계약과 갈린다.** `User.role` 계약 enum 은 `admin · operator · viewer` **3종**인데
 *    화면은 `service`(벤더 서비스 엔지니어)를 더해 4종을 쓴다. `service` 는 `view` 가 없어
 *    환자 식별자·시료 ID 가 마스킹되는 **유일한 역할**이라 화면에서 뺄 수 없다.
 *    → docs/fixtures.md 「계약 어긋남」 · 팀장 보고 대상.
 *
 * ⚠ `User` 에 **이메일 필드가 없다**. 계약은 `username` · `name` 만 준다.
 *   와이어프레임 표의 「이메일」 열은 근거가 없어 `USER_ROWS` 쪽 화면 전용 값으로 뺐다.
 */
import type { Session, User } from '@ctcells/contract'

export const USERS: User[] = [
  {
    user_id: 'usr-eric',
    username: 'eric.cho',
    name: 'eric.cho',
    role: 'admin',
    status: 'active',
    last_login_at: '2026-09-11T14:19:12+09:00',
    locked_until: null,
    version: 4,
  },
  {
    user_id: 'usr-hyungwoo',
    username: 'hyungwoo.cho',
    name: '조형우',
    role: 'operator',
    status: 'active',
    last_login_at: '2026-09-11T14:08:00+09:00',
    locked_until: null,
    version: 2,
  },
  {
    // 🔴 계약 enum 에 없는 역할. 그런데 이 역할만 `view` 가 없어 마스킹 화면을 만든다
    user_id: 'usr-seojun',
    username: 'seojun',
    name: '서준',
    role: 'service',
    status: 'active',
    last_login_at: '2026-09-10T13:55:00+09:00',
    locked_until: null,
    version: 1,
  },
  {
    // 잠김 — 🔴 `status` 만 보면 안 된다. 해제는 경과 후 첫 로그인 시도에 일어난다.
    //    `locked_until` 이 과거면 「해제됨 — 다음 로그인 시 활성」이다
    user_id: 'usr-minji',
    username: 'minji',
    name: '민지',
    role: 'viewer',
    status: 'locked',
    last_login_at: '2026-09-08T10:12:00+09:00',
    locked_until: '2026-09-11T15:00:00+09:00',
    version: 3,
  },
]

/**
 * 사용자 표 한 줄. 🔴 이메일은 계약 응답에 없다.
 *
 * ⚠ 세션 수(`session_count`)는 2026-09-30 에 뺐다 — 표의 「세션」 열이 없어졌다(eric). 계약에도 없던 값이다.
 *
 * 🔴 **`user_id` 를 반드시 싣는다.** 상세 화면이 세션·감사를 거르는 축이 `user_id` 다.
 *    이게 없으면 화면이 fixture 를 뒤져 `username → user_id` 를 찾게 되고, 실물에는 그 fixture 가
 *    없으므로 **세션 표와 감사 표가 통째로 빈다.** 표시값만 담는 행이 아니라 **식별자를 함께 나르는
 *    행**이다.
 */
export interface UserRow {
  /** 🔴 실물은 UUID, 개발 계정은 `"dev-admin"` 같은 문자열이다. 좁히지 않는다 */
  user_id: string
  username: string
  /**
   * 표시 이름(`User.name`). 🔴 **여기 이메일을 되살리지 마라** — `User` 스키마에 그 필드가
   * «없다»(정본·실서버 둘 다 실측 2026-09-20). 예전에는 이 자리가 `email` 이었고 실물에서는
   * 항상 `—` 였다. 계약에 있는 값으로 바꾼 것이다.
   */
  name: string
  role: User['role']
  status: User['status']
  last_seen_text: string
  /** 원값 그대로 나른다. 표기 변환은 화면이 한다 */
  last_login_at?: string | null
  /** ⚠ `status` 만 보면 안 된다 — 해제는 경과 뒤 첫 로그인 시도에 일어난다 */
  locked_until?: string | null
}

export const USER_ROWS: UserRow[] = [
  {
    user_id: 'usr-eric',
    username: 'eric.cho',
    name: '조에릭',
    role: 'admin',
    status: 'active',
    last_seen_text: '방금',
    last_login_at: '2026-09-11T14:19:12+09:00',
    locked_until: null,
  },
  {
    user_id: 'usr-hyungwoo',
    username: 'hyungwoo.cho',
    name: '조형우',
    role: 'operator',
    status: 'active',
    last_seen_text: '12분 전',
    last_login_at: '2026-09-11T14:08:00+09:00',
    locked_until: null,
  },
  {
    user_id: 'usr-seojun',
    username: 'seojun',
    name: '김서준',
    role: 'service',
    status: 'active',
    last_seen_text: '어제',
    last_login_at: '2026-09-10T13:55:00+09:00',
    locked_until: null,
  },
  {
    user_id: 'usr-minji',
    username: 'minji',
    name: '이민지',
    role: 'viewer',
    status: 'locked',
    last_seen_text: '3일 전',
    last_login_at: '2026-09-08T10:12:00+09:00',
    locked_until: '2026-09-11T15:00:00+09:00',
  },
]

/**
 * 지금 로그인한 계정 — 콘솔 헤더·패드 머리줄의 사용자 영역이 쓰는 값. `usr-eric`(위 `USERS[0]`) 기준이다.
 *
 * 🔴 **`session_remaining_text` 를 되살리지 마라**(2026-09-21 걷었다 · eric 지시 「저거 시간 그냥 빼자」).
 *    와이어프레임은 자동 로그아웃까지 남은 시간을 `02:59:32` 로 찍었는데(HTML 1108행) 계약에 없는
 *    값이라 **실물에서는 늘 `—`** 였다 — demo 에서만 그럴듯해 보이던 자리다. 서버가 주는 것은
 *    `Session.expires_at`(**절대 만료**)뿐이고 무활동 잔여(UAM-SES-06)와 다른 값이다 → **BR-53**.
 *    되살릴 때도 **프론트에서 타이머를 돌려 만들지 않는다**(DP-04).
 */
export interface CurrentAccount {
  name: string
  /** 🔴 계약 없음 — `User` 에 이메일 필드가 없다(위 `UserRow` 와 같은 사정) */
  email: string
  role: User['role']
}

export const CURRENT_ACCOUNT: CurrentAccount = {
  name: 'eric.cho',
  email: 'eric.cho@robos.one',
  role: 'admin',
}

/**
 * 세션 목.
 *
 * 🔴 **`ses-05` 에 `client` 키가 «없다» — 채우지 마라**(2026-09-20 실물 대조로 뺐다).
 *    `GET /sessions`(`app/views/sessions.py`)가 `model_dump(exclude_none=True)` 라
 *    접속 기기를 안 적은 세션은 **키째 빠져서 온다** — 실물 24건 중 8건이 그랬다.
 *    ⇒ 목이 전 건에 `client` 를 채우고 있으면 「없을 때 화면이 어떻게 되는가」가
 *    데모에서 한 번도 안 보인다(실제로 그 자리가 `—` 가 아니라 **빈 칸**이었다).
 */
export const SESSIONS: Session[] = [
  {
    session_id: 'ses-01',
    user_id: 'usr-eric',
    issued_at: '2026-09-14T08:40:00+09:00',
    expires_at: '2026-09-15T08:40:00+09:00',
    last_seen_at: '2026-09-11T14:20:00+09:00',
    revoked: false,
    client: 'tauri/win · 실험실 PC-1',
    version: 6,
  },
  {
    session_id: 'ses-02',
    user_id: 'usr-eric',
    issued_at: '2026-09-13T18:12:00+09:00',
    expires_at: '2026-09-14T18:12:00+09:00',
    last_seen_at: '2026-09-10T19:40:00+09:00',
    revoked: false,
    client: 'browser/mac · 사무실 노트북',
    version: 3,
  },
  {
    session_id: 'ses-03',
    user_id: 'usr-hyungwoo',
    issued_at: '2026-09-14T09:02:00+09:00',
    expires_at: '2026-09-15T09:02:00+09:00',
    last_seen_at: '2026-09-11T14:20:00+09:00',
    revoked: false,
    client: 'tauri/win · 실험실 PC-2',
    version: 5,
  },
  {
    session_id: 'ses-04',
    user_id: 'usr-hyungwoo',
    issued_at: '2026-09-14T11:20:00+09:00',
    expires_at: '2026-09-15T11:20:00+09:00',
    last_seen_at: '2026-09-11T14:08:00+09:00',
    revoked: false,
    client: 'browser/ipad · 원심분리기 패드',
    version: 2,
  },
  {
    session_id: 'ses-05',
    user_id: 'usr-seojun',
    issued_at: '2026-09-14T13:55:00+09:00',
    expires_at: '2026-09-15T13:55:00+09:00',
    last_seen_at: '2026-09-11T14:16:00+09:00',
    revoked: false,
    // ⚠ **`client` 키가 없다.** 위 머리말 참조 — 실물이 이 꼴을 준다. 되살리지 마라.
    version: 2,
  },
]
